import useAuth from './useAuth';
import { hasPermission, SystemFunction, Permission, UserRole } from '../lib/permissions/constants';

/**
 * Hook for checking user permissions based on their role
 * Uses function-based permissions instead of simple role checks for scalability
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const userRole = (user?.role as UserRole) || UserRole.USER;

  /**
   * Check if current user has permission for a function
   * @param func - The system function to check
   * @param level - Required permission level (default: READ)
   * @returns true if user has the required permission
   */
  const can = (
    func: SystemFunction,
    level: Permission = Permission.READ
  ): boolean => {
    return hasPermission(userRole, func, level);
  };

  /**
   * Check if user can EDIT (not just view)
   * @param func - The system function to check
   * @returns true if user can edit
   */
  const canEdit = (func: SystemFunction): boolean => {
    return hasPermission(userRole, func, Permission.EDIT);
  };

  /**
   * Check if user can only READ (not edit)
   * @param func - The system function to check
   * @returns true if user can read but not edit
   */
  const canOnlyRead = (func: SystemFunction): boolean => {
    const hasRead = hasPermission(userRole, func, Permission.READ);
    const hasEdit = hasPermission(userRole, func, Permission.EDIT);
    return hasRead && !hasEdit;
  };

  return {
    can,
    canEdit,
    canOnlyRead,
    userRole,

    // Convenience methods for common checks
    canManageOrganization: canEdit(SystemFunction.EDIT_ORGANIZATION),
    canManageBilling: canEdit(SystemFunction.MANAGE_BILLING),
    canManageTeam: canEdit(SystemFunction.INVITE_USERS),
    canManageIntegrations: canEdit(SystemFunction.MANAGE_INTEGRATIONS),
    canCreateProjects: can(SystemFunction.CREATE_PROJECTS),
    canEditProjects: canEdit(SystemFunction.EDIT_PROJECTS),
    canViewAnalytics: can(SystemFunction.VIEW_ANALYTICS),
  };
};

export default usePermissions;
